import { Injectable } from "@angular/core";
import { EChartsOption } from "echarts";
import { Observable, Subject } from "rxjs";
import {
    ClimateChart,
    ChartConfig,
    YAxis,
} from "../../../model/climate/climate-chart";
import { MAPTYCS_CHART_COLOR, getSSPColor } from "./custom-theme";
import { ClimateChartHelper } from "./util/climate-chart-helper";

@Injectable()
export class MaxTemperatureService {
    private readonly locationChartSubject: Subject<any>;
    private readonly addressChartSubject: Subject<any>;

    constructor() {
        this.locationChartSubject = new Subject<any>();
        this.addressChartSubject = new Subject<any>();
    }

    emitLocationChartEvent(chartInstance: any): void {
        this.locationChartSubject.next(chartInstance);
    }

    getLocationChartSubject(): Observable<any> {
        return this.locationChartSubject.asObservable();
    }

    emitAddressChartEvent(chartInstance: any): void {
        this.addressChartSubject.next(chartInstance);
    }

    getAddressChartSubject(): Observable<any> {
        return this.addressChartSubject.asObservable();
    }

    options: EChartsOption = {
        height: "80%",
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "cross",
                label: {
                    backgroundColor: "#6a7985",
                },
            },
        },
        legend: {},
        toolbox: ClimateChartHelper.getToolboxConfig('Maximum Temperature', 'Maximum_Temperature'),
        dataZoom: {
            show: false,
            start: 0,
            end: 100,
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "12%",
            top: "20%",
            containLabel: true,
        },
        xAxis: [
          { 
            type: 'category',
            boundaryGap: ['10%', '10%'],
            axisLabel: { interval: 0 },
          }
        ],
        yAxis: [
            {
                type: "value",
                scale: true,
                name: "Temperature",
                position: "left",
                alignTicks: true,
                axisLine: {
                    show: true,
                },
                axisLabel: {
                    formatter: "{value} °C",
                },
                boundaryGap: [0.2, 0.2],
            },
        ],
        series: [],
        dataset: {},
    };

    updateMaxTempChart(echartsInstance: any, climateChart: any) {
        const chartConfig: ChartConfig = climateChart.chartConfig;
        const yAxis: YAxis = chartConfig.yAxis;
        const seriesData = [];
        const source = [];

        source.push(climateChart.xAxis);

        for (const [key, value] of Object.entries(climateChart.series)) {
            seriesData.push(ClimateChartHelper.getSeriesConfig(key, chartConfig.type, getSSPColor));
            source.push(ClimateChartHelper.padSSPScenarioData(key, value as any[], climateChart.xAxis));
        }

        echartsInstance.setOption({
            animation: false,
            animationDuration: 0,
            animationDurationUpdate: 0,
            xAxis: [
              { 
                type: 'category',
                boundaryGap: ['10%', '10%'],
                axisLabel: { interval: 0 },
              }
            ],
            yAxis: [
                {
                    type: "value",
                    scale: true,
                    name: "Temperature",
                    min: yAxis.min,
                    max: yAxis.max,
                    interval: yAxis.interval,
                    position: "left",
                    alignTicks: true,
                    axisLine: {
                        show: true,
                    },
                    axisLabel: {
                        formatter: "{value} " + yAxis.unit,
                    },
                    boundaryGap: [0.2, 0.2],
                },
            ],
            dataset: {
                source: source,
            },
            series: seriesData,
        });
    
    // Attach download handler to add title only when downloading
    ClimateChartHelper.attachDownloadHandler(echartsInstance, 'Maximum Temperature', 'Maximum_Temperature');
        if (echartsInstance && typeof echartsInstance.on === 'function') {
          let applying = false;
          try { echartsInstance.off && echartsInstance.off('finished'); } catch {}
          echartsInstance.on('finished', () => {
            if (applying) return;
            applying = true;
            echartsInstance.setOption({
              xAxis: [{
                type: 'category',
                boundaryGap: ['10%', '10%'],
                axisLabel: { interval: 0 }
              }]
            }, { replaceMerge: ['xAxis'] });
            setTimeout(() => { applying = false; }, 0);
          });
        }
    }
}
